import * as R from 'ramda';
import { AxiosError, AxiosResponse } from 'axios';
import { Resolver } from '../../../config';
import RetryHandler, { DefaultRetryHandler } from './retryHandler';
import { RetryConfig } from '../../../@types/global.d';
import { ServiceErrorType, UserErrorResponse, UserResponseOptions } from '../serviceError';
import ServiceErrorProvider from '../serviceErrorProvider';

function getBackoffDelayMs(retryConfig: RetryConfig): number {
  const { networkRetryConfig } = Resolver.getServiceConfig();
  // If we're attempting more than the number of delays in the configuration array,
  // keep using the last delay value
  const backoffPosition = Math.min(
    retryConfig.totalRetriesAttempted || 0,
    Object.keys(networkRetryConfig.retryTimeoutMs).length,
  );

  const backoffDelayMs = networkRetryConfig.retryTimeoutMs[backoffPosition];
  return backoffDelayMs;
}

const delayRetry = async (retryConfig: RetryConfig): Promise<void> => {
  const backoffDelay = getBackoffDelayMs(retryConfig);
  // eslint-disable-next-line no-promise-executor-return
  await new Promise((resolve) => setTimeout(resolve, backoffDelay));
};

const responseHasValidationErrors = (response?: AxiosResponse): boolean => {
  if (!response) {
    return false;
  }

  const data: unknown = R.propOr(null, 'data', response);
  return (
    !R.isNil(data) &&
    R.has('errorCodes', data) &&
    R.propSatisfies(R.is(Array), 'errorCodes', data) &&
    R.propSatisfies(R.complement(R.isEmpty), 'errorCodes', data) &&
    R.propSatisfies(R.all(R.is(String)), 'errorCodes', data)
  );
};

const shouldRetryAutomatically = (retryConfig: RetryConfig): boolean => {
  // Only retry if retries are set,
  // we haven't exceeded the allowed attempts,
  // and we haven't reached the failsafe maximum allowed attempts
  const configuredRetries = retryConfig.retries || 0;
  const retriesAttempted = retryConfig.retriesAttempted || 0;
  const shouldRetry = configuredRetries > 0 && retriesAttempted < configuredRetries;
  return shouldRetry;
};

const shouldPromptUser = (retryConfig: RetryConfig): boolean => {
  // Prompt user with error when
  //   retries are enabled and exhausted
  //   or failure prompt is explicitly enabled
  const configuredRetries = retryConfig.retries || 0;
  const retriesAttempted = retryConfig.retriesAttempted || 0;
  const retriesEnabled = configuredRetries > 0;
  const retriesExhausted = retriesAttempted >= configuredRetries;
  const promptUserAllowed = retryConfig.enableFailurePrompt || false;
  return (retriesEnabled && retriesExhausted) || promptUserAllowed;
};

const updateConfigForAutomaticRetry = (retryConfig: RetryConfig): RetryConfig => {
  return {
    ...retryConfig,
    retriesAttempted: (retryConfig.retriesAttempted || 0) + 1,
    totalRetriesAttempted: (retryConfig.totalRetriesAttempted || 0) + 1,
  };
};

const updateConfigForUserRetry = (retryConfig: RetryConfig): RetryConfig => {
  const { networkRetryConfig } = Resolver.getServiceConfig();
  return {
    ...retryConfig,
    // Now start doing only ${userControlledRetries} retry at a time
    retries: networkRetryConfig.userControlledRetries,
    retriesAttempted: 0,
    retryLoopsAttempted: (retryConfig.retryLoopsAttempted || 0) + 1,
    totalRetriesAttempted: (retryConfig.totalRetriesAttempted || 0) + 1,
  };
};

const showUserErrorPrompt = async (retryConfig: RetryConfig): Promise<UserErrorResponse> => {
  const { networkRetryConfig } = Resolver.getServiceConfig();
  const retriesEnabled = (retryConfig.retries || 0) > 0;
  const retryLoopsRemaining = (retryConfig.retryLoopsAttempted || 0) < networkRetryConfig.maxAllowedRetryLoops;
  // User has the option to retry if retries are enabled and there are loops remaining
  const allowUserRetry = retriesEnabled && retryLoopsRemaining;
  const notifyUserOptions = allowUserRetry ? UserResponseOptions.all : UserResponseOptions.fail;
  const userResponse = await ServiceErrorProvider.notifyUser(ServiceErrorType.network, notifyUserOptions);
  // Always return fail if user is not allowed to retry
  return allowUserRetry ? userResponse : 'fail';
};

const defaultRetryHandler = new DefaultRetryHandler();
const performRetry = (customHandlers: RetryHandler[], error: AxiosError): Promise<AxiosResponse> => {
  const customHandler = R.find((handler: RetryHandler) => handler.canHandle(error), customHandlers);
  const handler = customHandler || defaultRetryHandler;
  return handler.retry(error);
};

export {
  delayRetry,
  responseHasValidationErrors,
  performRetry,
  shouldRetryAutomatically,
  shouldPromptUser,
  updateConfigForAutomaticRetry,
  updateConfigForUserRetry,
  showUserErrorPrompt,
};
